import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { IAuthenticatedImage } from './abstractions';

const authenticatedImageEntityAdapter = createEntityAdapter<IAuthenticatedImage, string>(
  {
    selectId: (img) => img.path,
  }
);

const adapterSelectors = authenticatedImageEntityAdapter.getSelectors();
export const authenticatedImageSlice = createSlice({
  name: 'authenticatedImages',
  initialState: authenticatedImageEntityAdapter.getInitialState(),
  reducers: {
    authenticatedImageLoading(state, action: PayloadAction<{ path: string }>) {
      return authenticatedImageEntityAdapter.setOne(state, {
        path: action.payload.path,
        type: 'loading'
      });
    },
    authenticatedImageLoadFailed(state, action: PayloadAction<{ path: string, error: string }>) {
      return authenticatedImageEntityAdapter.setOne(state, {
        type: 'error',
        path: action.payload.path,
        error: action.payload.error
      });
    },
    authenticatedImageLoaded(state, action: PayloadAction<{ path: string, dataUri: string }>) {
      const current = state.entities[action.payload.path] as IAuthenticatedImage | undefined;
      if (current?.type === 'loaded') {
        // increment the refcount
        current.refCount += 1;
        return state;
      }

      return authenticatedImageEntityAdapter.setOne(state, {
        type: 'loaded',
        path: action.payload.path,
        refCount: 1,
        dataUri: action.payload.dataUri,
      });
    },
    authenticatedImageReferenced(state, action: PayloadAction<{ path: string }>) {
      const current = state.entities[action.payload.path] as IAuthenticatedImage | undefined;
      if (current?.type !== 'loaded') {
        // We'll ignore this since it is saying we're referencing an image that isn't loaded
        return state;
      }
      // increment the refcount
      current.refCount += 1;
      return state;
    },
    authenticatedImageUnreferenced(state, action: PayloadAction<{ path: string }>) {
      // 'as' Due to record being incorrectly typed for this use case
      const current = state.entities[action.payload.path] as IAuthenticatedImage | undefined;
      if (current?.type !== 'loaded') {
        // We'll ignore this since it is saying we're unloading an image that isn't loaded
        return state;
      }
      // decrement the refcount
      current.refCount -= 1;
      // TODO(nibeauli): sanity check this isn't going negative? 
      return state;
    },
    authenticatedImageRemoved(state, action: PayloadAction<{ path: string }>) {
      return authenticatedImageEntityAdapter.removeOne(state, action.payload.path);
    },
    authenticatedImageReplaced(state, action: PayloadAction<{ path: string, dataUri: string }>) {
      const current = state.entities[action.payload.path] as IAuthenticatedImage | undefined;
      if (current?.type !== 'loaded') {
        // We'll ignore this since it is saying we're replacing an image that isn't loaded
        return state;
      }
      // replace the dataUri
      current.dataUri = action.payload.dataUri;
      return state;
    }
  },
  selectors: {
    selectAuthenticatedImage: (state, path: string): (IAuthenticatedImage | undefined) => {
      return adapterSelectors.selectById(state, path);
    },
    selectUnreferencedImages: (state) => {
      return adapterSelectors.selectAll(state).filter(img => img.type === 'loaded' && img.refCount <= 0)
    }
  }
});
